import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core'
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs'
import { MenuItemId } from 'src/app/shared/models/menu.model'
import { ProfileInstallation } from 'src/app/shared/services/backend'
import { MenuService } from 'src/app/shared/services/menu.service'
import { ProfileLoginService } from 'src/app/shared/services/profile-login.service'
import { Utils } from 'src/app/utils/utils'
import { InstallationItem } from '../../models/installation-item.model'
import { PaymentType } from '../../models/payment-type.model'
import { SubscribeTo } from '../../models/subscribe-to.model'
import { PrivateProfileService } from '../../services/private-profile.service'
import { ProfileStateService } from '../../services/profile-state.service'

@Component({
    selector: 'app-private-profile',
    templateUrl: './private-profile.component.html',
    styleUrls: ['./private-profile.component.scss'],
    standalone: false,
})
export class PrivateProfileComponent implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {
    private static readonly CPR_UPDATE_PROPAGATION_DELAY_MS = 5000

    @Input() isInToolbar: boolean = false

    private subscription = new Subscription()

    private installationItem: InstallationItem | null = null
    private loading = new BehaviorSubject<boolean>(true)

    // Bound straight to the state service — single source of truth
    public installations$ = this.profileStateService.installations$
    public associatedUsers$ = this.profileStateService.associatedUsers$
    public paymentDetails$ = this.profileStateService.paymentDetails$
    public installationDetails$ = this.profileStateService.installationDetails$
    public supplierName$ = this.profileStateService.supplierName$
    public customerNumber$ = this.profileStateService.customerNumber$
    public loading$ = this.loading.asObservable()

    constructor(
        private profileService: PrivateProfileService,
        private profileLoginService: ProfileLoginService,
        private changeDetectorRef: ChangeDetectorRef,
        private profileStateService: ProfileStateService,
        public menuService: MenuService
    ) {}

    public ngOnInit() {
        if (!this.isInToolbar) {
            this.menuService.preselectedMenuItem(MenuItemId.profile)
        }
        this.getInstallations()
    }

    public ngAfterViewInit(): void {
        Utils.scrollToTop()
    }

    public ngAfterContentChecked() {
        this.changeDetectorRef.detectChanges()
    }

    public ngOnDestroy() {
        this.subscription.unsubscribe()
    }

    public onSelectInstallation(installationItem: InstallationItem) {
        this.profileService.setSelectedInstallation({
            customerNumber: installationItem.customerNumber,
            installationNumber: installationItem.selectedInstallation.installationNumber,
        })
        this.profileStateService.setSelectedInstallation(installationItem)
        this.installationItem = installationItem
        this.getAssociatedUsersInformation(installationItem.customerNumber, installationItem.selectedInstallation)
    }

    public onAddCustomerRelation() {
        this.profileLoginService.addCustomerRelation()
    }

    public onSubscribeTo(event: SubscribeTo) {
        if (event.type === PaymentType.paymentservice) {
            window.open(event.url, '_blank')
        } else {
            if (event?.cpr) {
                this.saveCprNumber(event.cpr, event.type)
            } else if (event.type === PaymentType.easyaccount) {
                this.subscribeToEasyAccount(true)
            } else {
                this.subscribeToDigitalPost(true)
            }
        }
    }

    private async waitForCprPropagation(): Promise<void> {
        await new Promise((resolve) => setTimeout(resolve, PrivateProfileComponent.CPR_UPDATE_PROPAGATION_DELAY_MS))
    }

    public onUnsubscribeFrom(type: PaymentType) {
        switch (type) {
            case PaymentType.easyaccount:
                this.subscribeToEasyAccount(false)
                break
            case PaymentType.digitalpost:
                this.subscribeToDigitalPost(false)
                break
        }
    }

    private async subscribeToEasyAccount(subscribe: boolean) {
        this.loading.next(true)
        const response = await firstValueFrom(
            this.profileService.subscribeToEasyAccount(this.installationItem.customerNumber, subscribe)
        )
        if (response) {
            this.onSelectInstallation(this.installationItem)
        }
    }

    private async subscribeToDigitalPost(subscribe: boolean) {
        this.loading.next(true)
        const response = await firstValueFrom(
            this.profileService.subscribeToDigitalPost(this.installationItem.customerNumber, subscribe)
        )
        if (response) {
            this.onSelectInstallation(this.installationItem)
        }
    }

    private async saveCprNumber(cpr: string, type: PaymentType) {
        this.loading.next(true)
        const response = await firstValueFrom(
            this.profileService.saveCprNumber(this.installationItem.customerNumber, cpr)
        )
        if (response) {
            await this.waitForCprPropagation()

            if (type === PaymentType.easyaccount) {
                await this.subscribeToEasyAccount(true)
            } else {
                await this.subscribeToDigitalPost(true)
            }
        }
    }

    public reload() {
        window.location.reload()
    }

    private async getInstallations() {
        this.loading.next(true)
        const installations = await firstValueFrom(this.profileService.getInstallations())
        this.profileStateService.setInstallations(installations)
        this.loading.next(false)
    }

    private getAssociatedUsersInformation(customerNumber: string, installation: ProfileInstallation) {
        this.loading.next(true)
        this.subscription.add(
            this.profileService
                .getAssociatedUsersInformation(customerNumber, installation.installationNumber)
                .subscribe((response) => {
                    if (response) {
                        this.profileStateService.setCustomerNumber(customerNumber)
                        this.profileStateService.setAssociatedUsers({
                            secondaryName: response.associatedUsers.secondaryName,
                            primary: {
                                ...response.associatedUsers.primary,
                                isBusiness: false,
                            },
                        })

                        this.profileStateService.setPaymentDetails({
                            paymentServiceActive: response.paymentServiceActive,
                            paymentServiceUrl: response.paymentServiceUrl,
                            nemKontoActive: response.nemKontoActive,
                            digitalPostActive: response.digitalPostActive,
                            birthDate: response.associatedUsers?.primary.birthDate,
                            isBusiness: false,
                        })

                        this.profileStateService.setInstallationDetails({
                            meterNumber: installation.meterNumber,
                            gasSupplyStartDate: response.gasSupplyStartDate,
                            meteringPoint: response.meteringPoint,
                            supplierName: response.supplierName ?? '',
                            correctorNumber: response.correctorNumber,
                            installationNumber: installation.installationNumber,
                            displayReadingPlan: installation.displayReadingPlan,
                        })

                        this.profileStateService.setSupplierName(response.supplierName ?? '')
                    }
                    this.loading.next(false)
                })
        )
    }
}
